import {reactFetchJSON} from '@github-ui/verified-fetch'

const BUG_REPORT_REPOSITORY = 'github/copilot-experiences'
const BUG_REPORT_LABELS = ['bug', 'copilot-on-web']
const MAX_DEBUG_LOG_CHARS = 30_000
const POLL_INTERVAL_MS = 1_000
const POLL_ATTEMPTS = 60

interface BulkCreateJobStatus {
  job: {
    state: 'pending' | 'queued' | 'started' | 'success' | 'error'
    error_message: string | null
    completed_issues: Array<{href: string}>
  }
}

function truncateDebugLog(debugLog: string): string {
  if (debugLog.length <= MAX_DEBUG_LOG_CHARS) return debugLog
  // The tail holds whatever went wrong most recently, so drop from the front.
  return `…truncated…\n${debugLog.slice(-MAX_DEBUG_LOG_CHARS)}`
}

/** Log text is arbitrary, so the fence has to outrun any backtick run inside it. */
function fenceFor(content: string): string {
  const longestRun = Math.max(0, ...Array.from(content.matchAll(/`+/g), match => match[0].length))
  return '`'.repeat(Math.max(3, longestRun + 1))
}

export function buildBugReportBody({sessionId, debugLog}: {sessionId?: string; debugLog: string}): string {
  const sections = [
    '## Debug context',
    '',
    `- Session ID: ${sessionId ?? 'unknown'}`,
    `- URL: ${window.location.href}`,
    `- User agent: ${navigator.userAgent}`,
  ]

  if (debugLog) {
    const log = truncateDebugLog(debugLog)
    const fence = fenceFor(log)
    sections.push('', '## Debug log', '', fence, log, fence)
  }

  return sections.join('\n')
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as {error?: string | {message?: string}}
    if (typeof data.error === 'string') return data.error
    if (data.error?.message) return data.error.message
  } catch {
    // Fall through to the status-based message.
  }
  return `Request failed with status ${response.status}.`
}

/**
 * Creates a single issue in the bug report repository and resolves with its URL.
 *
 * `/copilot_issues/bulk_create` is the only client-reachable issue-create
 * endpoint that takes a repository by name-with-owner, and it runs as a job, so
 * the issue URL only becomes available by polling the returned status URL.
 */
export async function createBugReportIssue(
  {title, body}: {title: string; body: string},
  {intervalMs = POLL_INTERVAL_MS, attempts = POLL_ATTEMPTS}: {intervalMs?: number; attempts?: number} = {},
): Promise<string> {
  const response = await reactFetchJSON('/copilot_issues/bulk_create', {
    method: 'POST',
    body: {
      data: {
        issues: [
          {
            // The tag identifies the issue within the job, and reusing one
            // across calls makes the backend hand back the earlier issue.
            tag: `staff-bug-report-${crypto.randomUUID()}`,
            repository_nwo: BUG_REPORT_REPOSITORY,
            title,
            body,
            labels: BUG_REPORT_LABELS,
          },
        ],
      },
    },
  })
  if (!response.ok) throw new Error(await readErrorMessage(response))

  const {jobStatusUrl} = (await response.json()) as {jobStatusUrl: string}

  for (let attempt = 0; attempt < attempts; attempt++) {
    await new Promise(resolve => setTimeout(resolve, intervalMs))

    const statusResponse = await reactFetchJSON(jobStatusUrl)
    if (!statusResponse.ok) throw new Error(await readErrorMessage(statusResponse))

    const {job} = (await statusResponse.json()) as BulkCreateJobStatus
    if (job.state === 'error') throw new Error(job.error_message ?? 'Issue creation failed.')
    if (job.state === 'success') {
      const href = job.completed_issues[0]?.href
      if (!href) throw new Error('Issue creation reported success but returned no issue.')
      return new URL(href, window.location.origin).toString()
    }
  }

  throw new Error('Timed out waiting for the issue to be created.')
}
